import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { mq } from '../../../../../../common/src/utils/styledComponentsUtils';

// Components
import SingleAccident from './SingleAccident';
import DashedButton from '../../../ui/advisory-flow/components/shared-components/DashedButton';
import { Alert } from 'wg-fe-ui';

// Redux

// Types
import { Accident } from '../../../../../../../types/ProductFactory';
import { Change } from '../../../../../../../types/Form';
import { Grid, useBreakpointValue } from '@wegroup/design-system';
import { useFlowsDispatch } from '../../../../../../common/src/utils/globalVariables';
import { setToastErrorMessage } from '../../../../store/redux/slices/flowsSlice';
import AccidentEditModal from './AccidentEditModal';
interface Props {
  name: string;
  accidents: Accident[];
  handleChange: (arg0: Change) => void;
}

const AccidentsInput: React.FC<React.PropsWithChildren<Props>> = ({
  name,
  accidents,
  handleChange,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const flowsDispatch = useFlowsDispatch();

  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState<number[]>([]);

  useEffect(() => {
    if (isEditing.length === 0 && !isAdding)
      flowsDispatch(setToastErrorMessage(t('')));
    else {
      flowsDispatch(
        setToastErrorMessage(
          t('Please finish editing or adding the accidents'),
        ),
      );
    }
  }, [isEditing, isAdding]);

  useEffect(() => {
    return () => {
      setIsEditing([]);
    };
  }, []);

  const handleSave = (accident: Accident) => {
    setIsAdding(false);
    handleChange({
      name: name,
      value: [...accidents, accident],
    });
  };

  const handleEdit = (accident: Accident, index: number) => {
    if (index > -1) {
      const newAccidents = [...accidents];
      newAccidents[index] = accident;
      handleChange({
        name: name,
        value: newAccidents,
      });
    }
  };

  const handleRemove = (index: number) => {
    handleChange({
      name: name,
      value: accidents.filter((_, _index) => _index !== index),
    });
    handleIsEditing(false, index);
  };

  const handleIsEditing = (_isEditing: boolean, index: number) => {
    if (_isEditing) setIsEditing((prev) => [...prev, index]);
    else setIsEditing((prev) => prev.filter((_index) => _index !== index));
  };

  const isMobile = !!useBreakpointValue({ base: true, laptop: false });

  return (
    <>
      {isMobile && (
        <StyledAlert icon type="warning">
          {t('Tap one of your accidents in order to edit or remove them')}
        </StyledAlert>
      )}
      <Grid
        width="100%"
        templateColumns={{ base: '1fr', mobileL: 'repeat(auto-fill, 22rem)' }}
        gridGap="6"
        justifyContent="center"
        data-test-id="ProdFac_AccidentsInputComponent"
        {...otherProps}
      >
        {accidents.map((accident, index) => (
          <SingleAccident
            key={`accident_${index}`}
            accident={accident}
            isMobile={isMobile}
            handleEdit={(a: Accident) => handleEdit(a, index)}
            handleRemove={() => handleRemove(index)}
            isEditing={isEditing.includes(index)}
            setIsEditing={(_isEditing: boolean) =>
              handleIsEditing(_isEditing, index)
            }
          />
        ))}
        <AccidentEditModal
          isOpen={isAdding}
          onSave={handleSave}
          onCancel={() => setIsAdding(false)}
        />
        <StyledDashedButton
          label={t('Add a new accident')}
          onClick={() => setIsAdding(true)}
        />
      </Grid>
    </>
  );
};

const StyledAlert = styled(Alert)`
  padding: 1.6rem;
  margin-bottom: 2.4rem;
  width: unset;

  > div {
    font-size: 1.4rem;
  }
`;

const StyledDashedButton = styled(DashedButton)`
  flex-direction: row;
  align-items: center;
  padding: 1.6rem;
  height: fit-content;

  > div {
    margin-left: 1.6rem;
    margin-top: 0;
  }

  > button {
    padding: 0.2rem;
    height: 3.5rem;
    width: 3.5rem;
  }

  ${mq.mobileL} {
    flex-direction: column;
    padding: 4rem;
    width: 22rem;
    min-height: 20rem;
    height: 100%;

    > div {
      margin-left: 0;
      margin-top: 1.6rem;
    }

    > button {
      height: 5rem;
      width: 5rem;
      padding: 0.8rem;
    }
  }
`;

export default AccidentsInput;
